import { t } from "i18next";
import { useEffect, useRef } from "react";
import { AppConfig } from "@/common/appConfig";
import { useUpdateNoteTemplate } from "@/hooks/notesTemplates/useUpdateNoteTemplate";
import { toast } from "@/hooks/use-toast";

// Saves an additional visit note (referral letter, patient summary, extra
// note) with the success and failure toasts. Shared by the classic card and
// the redesigned workspace.
export const useVisitNoteSave = (
  visitPublicId: string,
  notePublicId: string,
) => {
  const { updateTemplate, result, loading, error } =
    useUpdateNoteTemplate(visitPublicId);
  const quiet = useRef(false);

  useEffect(() => {
    if (!loading) {
      if (error) {
        toast({
          variant: "error",
          title: t("visit.messages.updateFailed"),
        });
      }
      // A save that asked to stay quiet (the review card advances on its
      // own) shows no success toast; failures always do.
      if (result && !quiet.current) {
        toast({
          variant: "success",
          // v3.2: a short "Saved" pill.
          title: AppConfig.enableAppRedesign
            ? t("v3.toast.saved")
            : t("visit.messages.updateSuccessful"),
        });
      }
    }
  }, [error, loading, result]);

  // True when saved (see useVisitWrites).
  const save = (
    value: string,
    options: { quiet?: boolean } = {},
  ): Promise<boolean> => {
    quiet.current = !!options.quiet;
    return updateTemplate({ result: value, public_id: notePublicId });
  };

  return { save, saving: loading };
};
